import { AbstractDataProviderV2, IDataProviderV2, IItem } from 'components/system/data-provider';
import { A9Service } from 'components/system/angular/A9Service';
import { RsCustomerClub } from 'common/customers/services/types/RsCustomerClub';
import { RsCustomerClubInfo } from 'common/customers/services/types/RsCustomerClubInfo';
import { CustomerClubAdd } from 'common/customers/services/types/CustomerClubAdd';
import { CustomerClubUpdate } from 'common/customers/services/types/CustomerClubUpdate';
import { customerClubType } from 'common/customers/services/types';

export interface ICustomerClubDataProvider extends IDataProviderV2<RsCustomerClub, RsCustomerClubInfo, CustomerClubAdd, CustomerClubUpdate> {
    getSecondClub(customerId): Promise<RsCustomerClub>;

    getHomeClub(customerId): Promise<RsCustomerClub>;

    changeSecondClub(
        customerId: number,
        customerClubId: number,
        secondClubId: number,
        numberInSecondClub: number,
        secondClubName: string
    ): Promise<void>;

    unsetSecondClub(customerId: number): Promise<void>;

    addSecondClub(customerId: number, secondClubId: number, numberInSecondClub: number, secondClubName: string): Promise<void>;
}

export interface ICustomerClub extends IItem {
    active: boolean;
    clubId: number;
    clubName: string;
    endDate: string;
    notes: string;
    numberInClub: number;
    startDate: string;
    type: customerClubType;
}

@A9Service('customerClubDataProvider')
export class CustomerClubDataProvider
    extends AbstractDataProviderV2<RsCustomerClub, RsCustomerClubInfo, CustomerClubAdd, CustomerClubUpdate>
    implements ICustomerClubDataProvider
{
    constructor() {
        super();
        this.setApiUrl('middle://rs/customers/{customerId}/clubs');
    }

    public patch() {
        return Promise.reject('Method `patch` is not available');
    }

    public changeSecondClub(
        customerId: number,
        customerClubId: number,
        secondClubId: number,
        numberInSecondClub: number,
        secondClubName: string
    ): Promise<void> {
        return this.getSecondClub(customerId)
            .then(second => {
                if (second) {
                    return this.get({ customerId: customerId, id: customerClubId }).then(newSecondClub => {
                        second.type = customerClubType.Other;
                        newSecondClub.type = customerClubType.Second;
                        return this.update(second, { customerId: customerId, id: second.id }).then(() =>
                            this.update(newSecondClub, { customerId: customerId, id: newSecondClub.id })
                        );
                    });
                }
                return this.get({ customerId: customerId, id: customerClubId }).then(newSecondClub => {
                    if (newSecondClub) {
                        newSecondClub.type = customerClubType.Second;
                        return this.update(newSecondClub, { customerId: customerId, id: newSecondClub.id });
                    }
                    const item = {
                        id: 0,
                        type: customerClubType.Second,
                        clubId: secondClubId,
                        numberInClub: numberInSecondClub || 0,
                        active: true,
                        startDate: null,
                        endDate: null,
                        notes: '',
                        clubName: secondClubName || ''
                    };
                    return this.add(item, { customerId: customerId });
                });
            })
            .then(() => {});
    }

    public unsetSecondClub(customerId: number): Promise<void> {
        return this.getSecondClub(customerId).then(second => {
            if (second) {
                second.type = customerClubType.Other;
                return this.update(second, { customerId: customerId, id: second.id }).then(() => {});
            }
        });
    }

    public addSecondClub(customerId: number, secondClubId: number, numberInSecondClub: number, secondClubName: string): Promise<void> {
        return this.getSecondClub(customerId)
            .then(second => {
                if (second) {
                    second.clubId = secondClubId;
                    second.numberInClub = numberInSecondClub || 0;
                    second.active = true;
                    second.startDate = null;
                    second.endDate = null;
                    second.notes = '';
                    second.clubName = secondClubName || '';

                    return this.update(second, { customerId: customerId, id: second.id });
                }
                const item = {
                    id: 0,
                    type: customerClubType.Second,
                    clubId: secondClubId,
                    numberInClub: numberInSecondClub || 0,
                    active: true,
                    startDate: null,
                    endDate: null,
                    notes: ''
                };
                return this.add(item, { customerId: customerId });
            })
            .then(() => {});
    }

    public getSecondClub(customerId): Promise<RsCustomerClub> {
        return this.getClub(customerId, customerClubType.Second);
    }

    public getHomeClub(customerId): Promise<RsCustomerClub> {
        return this.getClub(customerId, customerClubType.Home);
    }

    private getClub(customerId, type): Promise<RsCustomerClub> {
        return this.all({}, { customerId: customerId }).then(items => {
            return items.find(item => item.type === type);
        });
    }
}
